import { useCallback, useEffect, useRef, useState } from 'react';
import { DashboardService } from '../services/DashboardService';
import type { PaginationInfo } from '../types';
import type { DashboardDelinquentRow } from '../types/dashboard';

const SEARCH_DEBOUNCE_MS = 400;

const EMPTY_PAGINATION: PaginationInfo = {
  page: 1,
  pageSize: 20,
  totalRecords: 0,
  totalPages: 0,
  hasNextPage: false,
  hasPreviousPage: false,
};

export interface DashboardDelinquentListQuery {
  page: number;
  pageSize: number;
  searchName: string;
  paymentGateway: readonly string[];
  paidStart: string;
  paidEnd: string;
  enabled: boolean;
}

export interface UseDashboardDelinquentsReturn {
  rows: DashboardDelinquentRow[];
  pagination: PaginationInfo;
  loading: boolean;
  error: string | null;
  patchDelinquent: (
    studentId: number,
    patch: Partial<Pick<DashboardDelinquentRow, 'followUpStage' | 'followUpNotes'>>,
  ) => void;
}

export function useDashboardDelinquents(
  query: DashboardDelinquentListQuery,
): UseDashboardDelinquentsReturn {
  const [rows, setRows] = useState<DashboardDelinquentRow[]>([]);
  const [pagination, setPagination] = useState<PaginationInfo>(EMPTY_PAGINATION);
  const [loading, setLoading] = useState(query.enabled);
  const [error, setError] = useState<string | null>(null);
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const previousQuery = useRef<DashboardDelinquentListQuery | null>(null);
  const requestId = useRef(0);
  const queryRef = useRef(query);
  const gatewayKey = query.paymentGateway.join(',');
  queryRef.current = query;

  const patchDelinquent = useCallback((
    studentId: number,
    patch: Partial<Pick<DashboardDelinquentRow, 'followUpStage' | 'followUpNotes'>>,
  ) => {
    setRows((current) => current.map((row) => (
      row.studentId === studentId ? { ...row, ...patch } : row
    )));
  }, []);

  useEffect(() => {
    if (!query.enabled) {
      requestId.current += 1;
      setLoading(false);
      setError(null);
      return undefined;
    }

    const currentQuery = queryRef.current;
    const previous = previousQuery.current;
    const onlySearchChanged = Boolean(
      previous
      && previous.enabled
      && currentQuery.searchName !== previous.searchName
      && currentQuery.page === previous.page
      && currentQuery.pageSize === previous.pageSize
      && currentQuery.paidStart === previous.paidStart
      && currentQuery.paidEnd === previous.paidEnd
      && currentQuery.paymentGateway.join(',') === previous.paymentGateway.join(','),
    );
    previousQuery.current = currentQuery;

    const run = () => {
      const currentRequest = ++requestId.current;
      setLoading(true);
      setError(null);

      DashboardService.listDelinquents(queryRef.current)
        .then((page) => {
          if (currentRequest !== requestId.current) {
            return;
          }

          setRows(page.data);
          setPagination(page.pagination);
        })
        .catch((caught: unknown) => {
          if (currentRequest !== requestId.current) {
            return;
          }

          setRows([]);
          setPagination(EMPTY_PAGINATION);
          setError(caught instanceof Error ? caught.message : 'Erro ao carregar inadimplentes');
        })
        .finally(() => {
          if (currentRequest === requestId.current) {
            setLoading(false);
          }
        });
    };

    clearTimeout(debounceTimer.current);
    if (onlySearchChanged) {
      debounceTimer.current = setTimeout(run, SEARCH_DEBOUNCE_MS);
    } else {
      run();
    }

    return () => clearTimeout(debounceTimer.current);
  }, [
    gatewayKey,
    query.enabled,
    query.page,
    query.pageSize,
    query.paidEnd,
    query.paidStart,
    query.searchName,
  ]);

  return {
    rows,
    pagination,
    loading,
    error,
    patchDelinquent,
  };
}
